import { useCallback, useEffect, useState } from 'react'
import AppStore from '@v4/store/appStore'
import useQuestionSettingsField from '@v4/hooks/questions/useQuestionSettingsField'
import useQuestionField from '@v4/hooks/questions/useQuestionField'
import ScorecardSelector from '@v4/utils/selectors/ScorecardSelector'
import QuizQuestionsSelector from '@v4/utils/selectors/QuizQuestionsSelector'
import TYPES from '@v4/constants/quiz'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'
import useStyles from './ImageButtons.styled'

export const useImageButtons = props => {
	const { onClick, pending, justify, questionId, answers, type, invertColors } = props

	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const isQuestionBuilderMode = PageBuilderSelector.useIsQuestionsMode()
	const locked = ScorecardSelector.useIsQuestionsLocked()
	const {
		updateQuestionOption,
		deleteQuestionOption,
		addQuestionOption,
		sortQuestionOptions,
	} = QuizQuestionsSelector.useActions()
	const options = useQuestionField(questionId, 'options')
	const allowMultipleSelect = useQuestionSettingsField('allow_multiple_select')
	const showLabels = useQuestionSettingsField('show_labels')
	const imageFit = useQuestionSettingsField('answer_image_fit')
	const answersPerRow = useQuestionSettingsField('answers_per_row')
	const classes = useStyles(props)
	const multiple = isBuilderMode ? allowMultipleSelect : type === TYPES.MULTI_IMAGE_CHECKBOX

	const [isDragging, setIsDragging] = useState(false)

	const [selectedOptions, setSelectedOptions] = useState(() => {
		if (!multiple) {
			return {}
		}

		return _.reduce(
			answers,
			(acc, { option_id: optionId }) => {
				if (optionId && _.some(options, { id: optionId })) {
					acc[optionId] = true
				}
				return acc
			},
			{},
		)
	})

	const handleClick = useCallback(
		optionId => {
			setSelectedOptions(values =>
				multiple
					? {
							...values,
							[optionId]: !values[optionId],
					  }
					: {
							[optionId]: true,
					  },
			)
			if (!multiple) {
				onClick({ options: [optionId] })
			}
		},
		[multiple, onClick],
	)

	const handleLabelEdit = useCallback(
		option => {
			updateQuestionOption({ questionId, option })
		},
		[questionId, updateQuestionOption],
	)

	const handleSortStart = useCallback(() => {
		setIsDragging(true)
	}, [])

	const handleSortEnd = useCallback(
		({ oldIndex, newIndex }) => {
			setIsDragging(false)
			sortQuestionOptions({ questionId, oldIndex, newIndex })
		},
		[questionId, sortQuestionOptions],
	)

	const handleAdd = useCallback(() => {
		addQuestionOption({
			id: questionId,
			option: { option: '', asset: null },
		})
	}, [addQuestionOption, questionId])

	const handleDelete = useCallback(
		id => {
			deleteQuestionOption({ questionId, id })
		},
		[deleteQuestionOption, questionId],
	)

	const handleSubmit = useCallback(() => {
		onClick({ options: Object.keys(selectedOptions).filter(key => selectedOptions[key]) })
	}, [onClick, selectedOptions])

	useEffect(() => {
		if (!multiple) {
			return undefined
		}
		const handleKeyUp = ({ key }) => {
			if (key === 'Enter') {
				handleSubmit()
			}
		}

		document.addEventListener('keyup', handleKeyUp)
		return () => {
			document.removeEventListener('keyup', handleKeyUp)
		}
	}, [handleSubmit, multiple])

	return [
		{
			classes,
			pending,
			isDragging,
			multiple,
			showLabels,
			items: options,
			justify,
			isBuilderMode,
			isQuestionBuilderMode,
			editable: !locked && isBuilderMode && isQuestionBuilderMode,
			selectedOptions,
			questionId,
			imageFit,
			answersPerRow,
			invertColors,
		},
		{
			handleAdd,
			handleDelete,
			handleLabelEdit,
			handleSortStart,
			handleSortEnd,
			handleClick,
			handleSubmit,
		},
	]
}
